import type { LogEntry } from '@/core/observability_types'

export const LOG_LEVEL_OPTIONS = ['all', 'error', 'warn', 'info', 'debug'] as const
export type LevelFilter = typeof LOG_LEVEL_OPTIONS[number]

const LEVEL_LINE_REGEX: Record<Exclude<LevelFilter, 'all'>, string> = {
  error: 'level=error|(^|[^[:alnum:]_])(error|err|fatal|critical|panic|exception)([^[:alnum:]_]|$)',
  warn: 'level=warn(ing)?|(^|[^[:alnum:]_])warn(ing)?([^[:alnum:]_]|$)',
  info: 'level=info|(^|[^[:alnum:]_])info([^[:alnum:]_]|$)',
  debug: 'level=debug|(^|[^[:alnum:]_])debug([^[:alnum:]_]|$)',
}

const LEVEL_LABEL_REGEX: Record<Exclude<LevelFilter, 'all'>, string> = {
  error: '(?i)error|err|fatal|critical|panic|exception',
  warn: '(?i)warn|warning',
  info: '(?i)info',
  debug: '(?i)debug',
}

const LEVEL_LABELS = ['level', 'severity', 'detected_level'] as const

function escapeLabelValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function escapeLineFilter(value: string): string {
  return escapeLabelValue(value)
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function escapeRegexFilter(value: string): string {
  return escapeLabelValue(escapeRegex(value))
}

function selector(labels: string[]): string {
  return `{${labels.join(',')}}`
}

export function parseLogLevel(line: string, labels: Record<string, string>): LogEntry['level'] {
  const lvl = labels['level'] || labels['severity'] || labels['detected_level'] || ''
  if (lvl) {
    const lower = lvl.toLowerCase()
    if (lower.includes('error') || lower.includes('err')) return 'error'
    if (lower.includes('warn')) return 'warn'
    if (lower.includes('info')) return 'info'
    if (lower.includes('debug')) return 'debug'
  }
  const lower = line.toLowerCase()
  if (lower.includes('"level":"error"') || lower.includes('level=error')) return 'error'
  if (lower.includes('"level":"warn"') || lower.includes('level=warn')) return 'warn'
  if (lower.includes('"level":"debug"') || lower.includes('level=debug')) return 'debug'
  if (/\b(error|err|fatal|critical|panic|exception)\b/.test(lower)) return 'error'
  if (/\b(warn|warning)\b/.test(lower)) return 'warn'
  if (/\bdebug\b/.test(lower)) return 'debug'
  return 'info'
}

export function buildLogQueries(service: string, level: LevelFilter, route: string): string[] {
  const trimmedRoute = route.trim()
  const selectors = service === '__all__'
    ? [trimmedRoute ? 'namespace=~"apisix|apisix-system"' : 'namespace=~".*agion.*"']
    : [`app="${escapeLabelValue(service)}"`]

  const shouldFilterRoute = trimmedRoute !== '' && service === '__all__'
  const routeFilter = shouldFilterRoute
    ? ` |= "${escapeLineFilter(trimmedRoute)}"`
    : ''
  if (level === 'all') {
    return [`${selector(selectors)}${routeFilter}`]
  }

  const labelRegex = LEVEL_LABEL_REGEX[level]
  const labelQueries = LEVEL_LABELS.map(label =>
    `${selector([...selectors, `${label}=~"${labelRegex}"`])}${routeFilter}`
  )
  const lineQuery = shouldFilterRoute
    ? `${selector(selectors)} |~ "(?i)(?:${LEVEL_LINE_REGEX[level]}).*${escapeRegexFilter(trimmedRoute)}|${escapeRegexFilter(trimmedRoute)}.*(?:${LEVEL_LINE_REGEX[level]})"`
    : `${selector(selectors)} |~ "(?i)${LEVEL_LINE_REGEX[level]}"`
  return [...labelQueries, lineQuery]
}

export function formatLogQueries(queries: string[]): string {
  return queries.join('\n')
}
